import {
  Autocomplete,
  FormControl,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { ChangeEvent, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import CustomAutocompleteMulti from "../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import FormGridRow from "../../../common/components/functional/formGridRow/FormGridRow";
import { SpinnerButton } from "../../../common/components/functional/spinnerButton/SpinnerButton";
import {
  SelectLight,
  TextFieldLight,
} from "../../../common/components/styled/styled";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { EezyCompany } from "../../invoices/invoiceAPI";
import { fetchEezyCompanies } from "../../invoices/invoiceSlice";
import { selectActiveExpertUserOptions } from "../../user/expertUsersSlice";
import { formatCitiesToNameIdOptions } from "../helpers";
import { fetchCities } from "../settingsActions";
import { OrderQueueType } from "./orderQueueApi";

interface Props {
  isNewQueue?: boolean;
  onSubmit: (dirtyFields: [x: string]) => void;
  isLoading: boolean;
}

/*
See discussion in JIRA YLPE-1138

Added new orderqueue type "teamSpecific". Adding cities to team specific order queue is blocked when creating NEW orderqueues.
New orderqueues can be now created with this type, but the types do not exist in backend.
Backend decides the type of new orderqueue based on the payload.

If this type of orderqueue is created and then edited, it appears as type of "city" in the edit form and therefore cities can be added to it.
For now, this is ok, but edit form should be fixed when the backend supports types.
*/

export const OrderQueueForm = ({
  isNewQueue = false,
  onSubmit,
  isLoading,
}: Props) => {
  const dispatch = useAppDispatch();
  const { control, formState, watch } = useFormContext();
  const { t } = useTranslation();

  const [cityOptions, setCityOptions] = useState<AutocompleteType[]>([]);
  const [eezyCompanyOptions, setEezyCompanyOptions] = useState<EezyCompany[]>([
    {
      eezyCompanyId: "",
      name: "",
      businessId: "",
      isFranchise: false,
      costCenters: [],
    },
  ]);
  const memberOptions = useAppSelector((state) =>
    selectActiveExpertUserOptions(state),
  );

  const type = watch("type");
  const contracts = watch("contracts");

  const checkIfAbleToSave = () => {
    if (type === OrderQueueType.DEFAULT) return false;
    if (isNewQueue) {
      if (type === OrderQueueType.CITY) {
        return (
          !!watch("name") &&
          watch("cities").length > 0 &&
          watch("members").length > 0
        );
      } else if (type === OrderQueueType.EC) {
        return (
          !!watch("name") &&
          !!watch("eezyCompany") &&
          watch("cities").length > 0 &&
          watch("members").length > 0
        );
      } else if (type === OrderQueueType.TEAM_SPECIFIC) {
        return !!watch("name");
      } else {
        return false;
      }
    } else {
      return formState.isDirty;
    }
  };

  useEffect(() => {
    dispatch(fetchCities("")).then((action) => {
      if (isFulfilled(action)) {
        setCityOptions(formatCitiesToNameIdOptions(action.payload));
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (isNewQueue) {
      if (type === OrderQueueType.EC) {
        dispatch(fetchEezyCompanies({})).then(
          (action) =>
            isFulfilled(action) && setEezyCompanyOptions(action.payload),
        );
      }
    }
  }, [dispatch, isNewQueue, type]);

  return (
    <>
      <Grid container rowSpacing={1} columnSpacing={2}>
        <FormGridRow label={t("name")} labelSize={1.5} fieldSize={10.5}>
          <Controller
            control={control}
            name="name"
            render={({ field: { value, onChange } }) => (
              <FormControl fullWidth>
                <TextFieldLight
                  name="name"
                  value={value}
                  size="small"
                  onChange={(value: ChangeEvent<HTMLInputElement>) =>
                    onChange(value)
                  }
                />
              </FormControl>
            )}
          />
        </FormGridRow>
        <FormGridRow
          label={t("orderQueues:type")}
          labelSize={1.5}
          fieldSize={10.5}
        >
          <Controller
            control={control}
            name="type"
            defaultValue={""}
            render={({ field: { value, onChange } }) => (
              <FormControl fullWidth>
                <SelectLight
                  value={value}
                  disabled={!isNewQueue}
                  onChange={(event) => onChange(event.target.value as string)}
                  size="small"
                >
                  <MenuItem value={OrderQueueType.CITY}>{t("city")}</MenuItem>
                  <MenuItem value={OrderQueueType.EC}>
                    {t("orderQueues:eezyCompany")}
                  </MenuItem>
                  <MenuItem value={OrderQueueType.TEAM_SPECIFIC}>
                    {t("orderQueues:teamSpecific")}
                  </MenuItem>
                </SelectLight>
              </FormControl>
            )}
          />
        </FormGridRow>
        {type === OrderQueueType.EC ? (
          <FormGridRow
            label={t("orderQueues:eezyCompany")}
            labelSize={1.5}
            fieldSize={10.5}
          >
            <Controller
              control={control}
              name="eezyCompany"
              render={({ field: { value, onChange } }) => (
                <FormControl fullWidth>
                  <Autocomplete
                    value={value}
                    disabled={!isNewQueue}
                    size="small"
                    options={eezyCompanyOptions}
                    getOptionLabel={(option) => option?.name || ""}
                    onChange={(_: any, newValue: any) => onChange(newValue)}
                    renderInput={(params: any) => (
                      <TextField name="eezyCompany" {...params} />
                    )}
                  />
                </FormControl>
              )}
            />
          </FormGridRow>
        ) : null}
        {contracts?.length > 0 ? (
          <FormGridRow
            label={t("orderQueues:contracts")}
            labelSize={1.5}
            fieldSize={10.5}
          >
            <>
              {!isNewQueue ? (
                <>
                  <Typography>{t("orderQueues:contracts")}</Typography>
                  <Controller
                    control={control}
                    name="contracts"
                    render={({ field: { value, onChange } }) => (
                      <FormControl fullWidth>
                        <CustomAutocompleteMulti
                          name="contracts"
                          defaultValue={value}
                          disabled
                          options={[]}
                          onChange={() => {}}
                        />
                      </FormControl>
                    )}
                  />
                </>
              ) : null}
            </>
          </FormGridRow>
        ) : null}
        {type !== OrderQueueType.TEAM_SPECIFIC && (
          <FormGridRow label={t("cities")} labelSize={1.5} fieldSize={10.5}>
            <Controller
              control={control}
              name="cities"
              render={({ field: { value, onChange } }) => (
                <FormControl fullWidth>
                  <CustomAutocompleteMulti
                    name="cities"
                    defaultValue={value}
                    options={cityOptions}
                    onChange={(_: any, newValue: AutocompleteType[]) =>
                      onChange(newValue)
                    }
                  />
                </FormControl>
              )}
            />
          </FormGridRow>
        )}
        <FormGridRow
          label={t("orderQueues:experts")}
          labelSize={1.5}
          fieldSize={10.5}
        >
          <Controller
            control={control}
            name="members"
            render={({ field: { value, onChange } }) => (
              <FormControl fullWidth>
                <CustomAutocompleteMulti
                  name="members"
                  defaultValue={value}
                  options={memberOptions}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(_: any, newValue: AutocompleteType[]) =>
                    onChange(newValue)
                  }
                  setTagDisabledForRemovedOptions={true}
                />
              </FormControl>
            )}
          />
        </FormGridRow>
        <FormGridRow
          labelSize={1.5}
          fieldSize={10.5}
          GridFieldProps={{ textAlign: "right", mt: 1 }}
        >
          <SpinnerButton
            label={t("save")}
            onClick={() => onSubmit(formState.dirtyFields as [x: string])}
            disabled={!checkIfAbleToSave()}
            variant="contained"
            isLoading={isLoading}
            color="primary"
            sx={{ height: "36px", borderRadius: "20px" }}
          />
        </FormGridRow>
      </Grid>
    </>
  );
};
